import React, { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN } from "../theme";
import { LEARN_UI } from "../media/learnAssets";
import LearnCompanion from "../LearnCompanion";

const COLORS = [LEARN.color.blue, "#00CAFF", LEARN.color.sun, LEARN.color.coral, LEARN.color.mint, LEARN.color.lavender];

/**
 * A burst of confetti + a scaled-in sticker + optional message. Pure CSS/JS,
 * no canvas, so it composes over any game surface. Gated on reduced motion.
 */
export function ConfettiBurst({ count = 36, originX = 50, originY = 40, spread = 120, active = true, reduceMotion = false }) {
  const pieces = useMemo(
    () => Array.from({ length: count }, (_, i) => ({
      i,
      angle: (i / count) * Math.PI * 2 + Math.random() * 0.6,
      dist: 80 + Math.random() * spread,
      size: 6 + Math.random() * 8,
      color: COLORS[i % COLORS.length],
      round: i % 3 === 0,
      spin: (Math.random() - 0.5) * 900,
      dur: 0.9 + Math.random() * 0.7,
    })),
    [count, spread],
  );
  if (!active || reduceMotion) return null;
  return (
    <div aria-hidden="true" style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden", zIndex: 20 }}>
      {pieces.map((p) => (
        <motion.span
          key={p.i}
          initial={{ x: 0, y: 0, opacity: 1, rotate: 0, scale: 0.6 }}
          animate={{
            x: Math.cos(p.angle) * p.dist,
            y: Math.sin(p.angle) * p.dist * 0.7 + 160,
            opacity: [1, 1, 0],
            rotate: p.spin,
            scale: 1,
          }}
          transition={{ duration: p.dur, ease: [0.16, 0.8, 0.3, 1] }}
          style={{
            position: "absolute", left: `${originX}%`, top: `${originY}%`,
            width: p.size, height: p.round ? p.size : p.size * 1.7,
            background: p.color, borderRadius: p.round ? "50%" : 3,
          }}
        />
      ))}
    </div>
  );
}

/**
 * Full celebration moment: confetti, a bouncing sticker (gold star / trophy),
 * headline + detail, sticker CTA. Used by every completion + world ceremony.
 */
export default function Celebration({
  open,
  kind = "star", // star | trophy | ribbon
  title = "Great work!",
  detail,
  cta = "Next",
  onCta,
  secondary,
  onSecondary,
  companion = true,
  reduceMotion = false,
  children,
}) {
  const [burst, setBurst] = useState(0);
  useEffect(() => { if (open) setBurst((b) => b + 1); }, [open]);
  const sticker = kind === "trophy" ? "sticker_trophy" : kind === "ribbon" ? "sticker_ribbon" : "sticker_star";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="celebration"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            position: "fixed", inset: 0, zIndex: 100001,
            background: "rgba(11,26,51,0.42)", backdropFilter: "blur(6px)",
            display: "flex", alignItems: "center", justifyContent: "center", padding: 20,
            fontFamily: LEARN.font,
          }}
        >
          <ConfettiBurst key={burst} originY={38} reduceMotion={reduceMotion} />
          <motion.div
            initial={reduceMotion ? false : { scale: 0.7, y: 30, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 26 }}
            style={{
              position: "relative", width: "min(440px, 100%)", background: "#fff",
              borderRadius: LEARN.radius.xl, padding: "28px 26px 24px", textAlign: "center",
              boxShadow: LEARN.shadow.float, border: `2px solid ${LEARN.color.line}`,
            }}
          >
            <motion.img
              src={LEARN_UI(sticker)}
              alt=""
              aria-hidden="true"
              initial={reduceMotion ? false : { scale: 0, rotate: -30 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 14, delay: 0.08 }}
              style={{ width: 128, height: 128, objectFit: "contain", margin: "-84px auto 6px", display: "block", filter: "drop-shadow(0 10px 18px rgba(11,26,51,0.2))" }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
            <h2 style={{ margin: 0, fontSize: 28, fontWeight: 700, color: LEARN.color.ink, letterSpacing: "-0.01em" }}>{title}</h2>
            {detail && <p style={{ margin: "8px 0 0", fontSize: 16, color: LEARN.color.muted, lineHeight: 1.45 }}>{detail}</p>}
            {children}
            {companion && (
              <div style={{ position: "absolute", right: -40, bottom: -34, pointerEvents: "none" }}>
                <LearnCompanion size={150} pose="cheer" sticky />
              </div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 20, flexWrap: "wrap" }}>
              {secondary && (
                <button type="button" onClick={onSecondary} style={{ fontFamily: LEARN.font, fontWeight: 700, fontSize: 15, color: LEARN.color.ink, background: LEARN.color.blueSoft, border: "none", borderRadius: 999, padding: "13px 22px", cursor: "pointer" }}>
                  {secondary}
                </button>
              )}
              <button
                type="button"
                onClick={onCta}
                autoFocus
                style={{ fontFamily: LEARN.font, fontWeight: 700, fontSize: 17, color: "#fff", background: LEARN.color.blue, border: "none", borderRadius: 999, padding: "14px 36px", boxShadow: LEARN.shadow.sticker(), cursor: "pointer" }}
              >
                {cta}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
